import { AvatarSize } from '../avatar.types';
import { CSSProperties } from 'react';
import { JSX } from 'react';
import { Skeleton } from '../../../skeleton';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import MuiAvatar from '@mui/material/Avatar';
import { useTheme } from '../../../../../theme';
import { IconBase } from '@doxyme/icons';
import { maskDataDog } from '../../../../../utils';
import { AriaProps } from '../../../../../types';

const avatarSize: Record<AvatarSize, number> = {
  '3xl': 96,
  '2xl': 80,
  xl: 48,
  lg: 40,
  md: 32,
  sm: 24,
};

type BasicAvatarProperties = AriaProps & {
  src?: string;
  label?: string;
  fallback?: string | JSX.Element;
  size: AvatarSize;
  textColor?: CSSProperties['color'];
  forceIsLoading?: boolean;
};

export const BasicAvatar = ({
  src,
  label,
  fallback,
  size,
  forceIsLoading = false,
  ...rest
}: BasicAvatarProperties): JSX.Element => {
  const theme = useTheme();
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Track the previous src value to prevent unnecessary loading state resets on re-renders.
  // Without this, the useEffect would set isLoading=true on every render, causing infinite loading when images are cached.
  const [currentSrc, setCurrentSrc] = useState(src);

  // In cases where `src` is not immediately populated (e.g., during patient check-in),
  useEffect(() => {
    // Only update loading state if src actually changed
    if (src !== currentSrc) {
      setCurrentSrc(src);
      if (src) {
        setIsLoading(true);
        setHasLoadError(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [src, currentSrc]);

  const shouldUseFallback = hasLoadError || !Boolean(src);

  /**
   * The skeleton will show if:
   * 1. `forceIsLoading: true`: Initials will never display. Skeleton will render until a `src` is populated & loaded.
   * 2. `forceIsLoading: false`: Initials will display if `src` isn't populated. Will display while `src` is loading.
   */
  const shouldShowSkeleton =
    (forceIsLoading && !hasLoadError && !src) ||
    (Boolean(src) && isLoading && !hasLoadError);

  const typography: Record<AvatarSize, CSSProperties> = {
    '3xl': {
      ...theme.typography.titleH1,
      color: theme.text.generic.primary,
      fontWeight: theme.typography.fontWeightRegular,
    },
    '2xl': {
      ...theme.typography.titleH2,
      color: theme.text.generic.primary,
      fontWeight: theme.typography.fontWeightRegular,
    },
    xl: {
      ...theme.typography.titleH3,
      color: theme.text.generic.primary,
      fontWeight: theme.typography.fontWeightRegular,
    },
    lg: {
      ...theme.typography.bodyM,
      color: theme.text.generic.primary,
      fontWeight: theme.typography.fontWeightMedium,
    },
    md: {
      ...theme.typography.bodyS,
      color: theme.text.generic.primary,
      fontWeight: theme.typography.fontWeightMedium,
    },
    sm: {
      ...theme.typography.caption,
      color: theme.text.generic.primary,
      fontWeight: theme.typography.fontWeightRegular,
    },
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {shouldShowSkeleton && (
        <Skeleton
          aria-hidden
          variant='circular'
          width={avatarSize[size]}
          height={avatarSize[size]}
          css={{
            transform: 'rotate(15deg)',
          }}
          animation='wave'
        />
      )}
      <MuiAvatar
        {...rest}
        src={src}
        sx={{
          width: avatarSize[size],
          height: avatarSize[size],
          bgcolor:
            shouldUseFallback && Boolean(fallback)
              ? theme.avatar.background
              : 'transparent',
          ...typography[size],
          ...(shouldShowSkeleton && {
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: 0,
            pointerEvents: 'none',
          }),
        }}
        aria-hidden
        alt={label}
        onLoad={() => {
          setHasLoadError(false);
          setIsLoading(false);
        }}
        onError={() => {
          setHasLoadError(true);
          setIsLoading(false);
        }}
        className='avatar-icon'
        {...maskDataDog()}
      >
        {shouldUseFallback &&
          (Boolean(fallback) ? (
            fallback
          ) : (
            <IconBase viewBox='0 0 80 80' size={avatarSize[size]}>
              <g clipPath='url(#clip0_36351_306)'>
                <rect
                  width='80'
                  height='80'
                  fill={theme.icon.interactive.disabled}
                />
                <path
                  d='M52 30C52 36.6274 46.6274 42 40 42C33.3726 42 28 36.6274 28 30C28 23.3726 33.3726 18 40 18C46.6274 18 52 23.3726 52 30Z'
                  fill={theme.icon.onBrand}
                />
                <path
                  d='M40.0008 70C31.6983 70 24.1343 66.8381 18.4472 61.6529C17.6879 60.9605 17.641 59.79 18.3474 59.0437C23.6356 53.4567 31.1161 50 40.0008 50C48.8855 50 56.366 53.4567 61.6542 59.0437C62.3606 59.79 62.3137 60.9605 61.5544 61.6529C55.8673 66.8381 48.3033 70 40.0008 70Z'
                  fill={theme.icon.onBrand}
                />
              </g>
              <defs>
                <clipPath id='clip0_36351_306'>
                  <rect
                    width='80'
                    height='80'
                    rx='40'
                    fill={theme.icon.onBrand}
                  />
                </clipPath>
              </defs>
            </IconBase>
          ))}
      </MuiAvatar>
    </Box>
  );
};
